import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
} from "@mui/material";
import { activityLog } from "./api";
import { getToken } from "../../utils/utils";
import PaginationComponent from "../brands/Pagination";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import SortingArrow from "../../../src/assests/images/Sorting.svg";
interface ActivityLogItem {
  id: number;
  admin_id: number;
  date: string;
  type: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

interface ActivityDataTableProps {
  startDate: Date | null;
  endDate: Date | null;
  searchQuery: string;
}

const ActivityDataTable: React.FC<ActivityDataTableProps> = ({
  startDate,
  endDate,
  searchQuery,
}) => {
  const token = getToken();
  const [activityData, setActivityData] = useState<ActivityLogItem[] | null>(
    null
  );
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  useEffect(() => {
    fetchActivityApi();
  }, [startDate, endDate]);

  useEffect(() => {
    if (searchQuery) {
      setPage(1);
    }
  }, [searchQuery]);

  const fetchActivityApiDebounced = debounce(async () => {
    await fetchActivityApi();
  }, 500);

  const fetchActivityApiThrottled = throttle(async () => {
    await fetchActivityApi();
  }, 500);

  useEffect(() => {
    fetchActivityApiDebounced();
    return () => fetchActivityApiDebounced.cancel();
  }, [searchQuery]);

  useEffect(() => {
    if (!searchQuery) {
      fetchActivityApiThrottled();
    }
    return () => fetchActivityApiThrottled.cancel();
  }, [page]);

  const fetchActivityApi = async () => {
    try {
      const fromdate = startDate ? startDate.toISOString().split("T")[0] : "";
      const todate = endDate ? endDate.toISOString().split("T")[0] : "";

      const reqBody = {
        key: "",
        fromdate,
        todate,
        page: page - 1,
        limit: searchQuery ? 1000 : 10,
        searchQuery,
      };

      const res = await activityLog(reqBody, token);

      if (res?.data?.data) {
        const filteredData = res.data.data.filter((row: ActivityLogItem) =>
          row.type.toLowerCase().includes(searchQuery.toLowerCase())
        );

        setActivityData(filteredData);
        if (!searchQuery) {
          setTotalPages(Math.ceil(res.data.totalCount / 10));
        } else {
          setTotalPages(0);
        }
      } else {
        setActivityData([]);
        setTotalPages(0);
      }
    } catch (error) {
      console.error("Error fetching activity logs:", error);
      setActivityData([]);
    }
  };
  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy]! < a[orderBy]!) return -1;
    if (b[orderBy]! > a[orderBy]!) return 1;
    return 0;
  }

  function getComparator<Key extends keyof any>(
    order: "asc" | "desc",
    orderBy: Key
  ): (a: { [key in Key]: any }, b: { [key in Key]: any }) => number {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function stableSort<T>(array: T[], comparator: (a: T, b: T) => number): T[] {
    return [...array].sort(comparator);
  }


  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<keyof ActivityLogItem>("date");

  const handleSort = (property: keyof ActivityLogItem) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = stableSort(activityData ?? [], getComparator(order, orderBy));
  return (
    <Box sx={{ background: "#F9FBFC", padding: "30px 30px 100px", borderTop: "1px solid #E7E8E9" }}>
      <Paper
        elevation={3}
        sx={{
          boxShadow: "none",
          border: "1px solid rgb(223, 223, 223)",
          "& .MuiTableContainer-root": {
            boxShadow: "none",
          },
          "& .MuiPaper-root": {
            borderRadius: "0"
          }
        }}
      >
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ background: "#E4E7EC" }}>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "date"}
                    direction={orderBy === "date" ? order : "asc"}
                    onClick={() => handleSort("date")}
                    IconComponent={() => (
                      <img
                        src={SortingArrow}
                        alt="Sort Icon"
                        style={{ width: 16, height: 16, opacity: 1, marginLeft: "5px" }} // ensure icon is always visible
                      />
                    )}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "type"}
                    direction={orderBy === "type" ? order : "asc"}
                    onClick={() => handleSort("type")}
                    IconComponent={() => (
                      <img
                        src={SortingArrow}
                        alt="Sort Icon"
                        style={{ width: 16, height: 16, opacity: 1, marginLeft: "5px" }} // ensure icon is always visible
                      />
                    )}
                  >
                    Type
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "description"}
                    direction={orderBy === "description" ? order : "asc"}
                    onClick={() => handleSort("description")}
                    IconComponent={() => (
                      <img
                        src={SortingArrow}
                        alt="Sort Icon"
                        style={{ width: 16, height: 16, opacity: 1, marginLeft: "5px" }} // ensure icon is always visible
                      />
                    )}
                  >
                    Description
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedData.length > 0 ? (
                sortedData.map((row, index) => (
                  <TableRow key={row.id} sx={{ backgroundColor: index % 2 !== 0 ? "#F9FBFC" : "transparent", }}>
                    <TableCell sx={{ maxWidth: "0px" }}>{row.date}</TableCell>
                    <TableCell sx={{ maxWidth: "0px" }}>{row.type}</TableCell>
                    <TableCell sx={{ maxWidth: "0px" }}>{row.description}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No data available for the selected date range
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {!searchQuery && totalPages > 1 && (
        <Box sx={{ padding: "15px 0", background: "#fff", border: "1px solid #ccc5c5", borderTop: "none", borderBottomLeftRadius: "10px", borderBottomRightRadius: "10px", }}>
          <PaginationComponent
            page={page}
            limit={10}
            totalPages={totalPages}
            setPage={setPage}
          />
        </Box>
      )}
    </Box>
  );
};

export default ActivityDataTable;
